import AddRounded from '@mui/icons-material/AddRounded';
import AssignmentReturnRounded from '@mui/icons-material/AssignmentReturnRounded';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import PostAddRounded from '@mui/icons-material/PostAddRounded';
import ReceiptLongRounded from '@mui/icons-material/ReceiptLongRounded';
import UndoRounded from '@mui/icons-material/UndoRounded';
import {
  Box,
  Button,
  Chip,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { api, unwrap } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { DataTable, SearchField, type Column } from '../../components/DataTable';
import { Money } from '../../components/Money';
import { EmptyState, PageHeader } from '../../components/states';
import { StatusChip } from '../../components/StatusChip';
import { businessDate, money } from '../../lib/format';
import { useShop } from '../../shop/ShopProvider';
import { percent } from '../payments/allocation';
import { links } from '../payments/kit/labels';
import { AllocationBar, Cell2, DateWithParty, DocLink, ErrorState } from '../payments/kit/ui';
import type { CreditNoteRow, DebitNoteRow, PurchaseReturnRow, SalesReturnRow } from './api';
import { useExportSelection } from '../dataio/useExportSelection';
import { filesColumn, useAttachmentCounts } from '../attachments';

export type ReturnsTab = 'sales' | 'credit-notes' | 'purchase' | 'debit-notes';

const TABS: { value: ReturnsTab; label: string; perm: string }[] = [
  { value: 'sales', label: 'Sales returns', perm: 'sales.view' },
  { value: 'credit-notes', label: 'Credit notes', perm: 'sales.view' },
  { value: 'purchase', label: 'Purchase returns', perm: 'purchases.view' },
  { value: 'debit-notes', label: 'Debit notes', perm: 'purchases.view' },
];

const NEW: {
  label: string;
  hint: string;
  to: string;
  perm: string;
  icon: ReactNode;
}[] = [
  {
    label: 'Sales return',
    hint: 'Customer brings goods back',
    to: '/returns/sales/new',
    perm: 'sales.return',
    icon: <AssignmentReturnRounded fontSize="small" />,
  },
  {
    label: 'Credit note',
    hint: 'Lower an invoice without a return',
    to: '/returns/credit-notes/new',
    perm: 'sales.return',
    icon: <PostAddRounded fontSize="small" />,
  },
  {
    label: 'Purchase return',
    hint: 'Send goods back to a supplier',
    to: '/returns/purchase/new',
    perm: 'purchases.return',
    icon: <UndoRounded fontSize="small" />,
  },
  {
    label: 'Debit note',
    hint: 'A supplier credit on a bill',
    to: '/returns/debit-notes/new',
    perm: 'purchases.return',
    icon: <ReceiptLongRounded fontSize="small" />,
  },
];

export function ReturnsPage() {
  const [params, setParams] = useSearchParams();
  const { can } = useAuth();
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const tabs = TABS.filter((t) => can(t.perm));
  const requested = params.get('tab') as ReturnsTab | null;
  const tab = tabs.find((t) => t.value === requested)?.value ?? tabs[0]?.value ?? 'sales';
  const creatable = NEW.filter((n) => can(n.perm));

  return (
    <>
      <PageHeader
        title="Returns & notes"
        subtitle="Goods coming back from customers or going back to suppliers, and credits on invoices and bills."
        actions={
          creatable.length > 0 && (
            <>
              <Button
                variant="contained"
                startIcon={<AddRounded />}
                endIcon={<ExpandMoreRounded />}
                onClick={(e) => setAnchor(e.currentTarget)}
                aria-haspopup="menu"
              >
                New
              </Button>
              <Menu
                anchorEl={anchor}
                open={Boolean(anchor)}
                onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ paper: { sx: { mt: 1, minWidth: 260 } } }}
              >
                {creatable.map((n) => (
                  <MenuItem
                    key={n.to}
                    onClick={() => {
                      setAnchor(null);
                      void navigate(n.to);
                    }}
                  >
                    <ListItemIcon sx={{ color: 'primary.main' }}>{n.icon}</ListItemIcon>
                    <ListItemText
                      primary={n.label}
                      secondary={n.hint}
                      slotProps={{ primary: { sx: { fontWeight: 600 } } }}
                    />
                  </MenuItem>
                ))}
              </Menu>
            </>
          )
        }
      />
      <Tabs
        value={tab}
        onChange={(_, v: ReturnsTab) => setParams({ tab: v })}
        variant="scrollable"
        allowScrollButtonsMobile
        sx={{ mb: 2.5, borderBottom: 1, borderColor: 'divider' }}
      >
        {tabs.map((t) => (
          <Tab key={t.value} value={t.value} label={t.label} />
        ))}
      </Tabs>
      {tab === 'sales' && <SalesReturnsList />}
      {tab === 'credit-notes' && <NotesList kind="credit" />}
      {tab === 'purchase' && <PurchaseReturnsList />}
      {tab === 'debit-notes' && <NotesList kind="debit" />}
    </>
  );
}

/** List state kept in the URL (search, status, page) next to the tab. */
function useListParams() {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const status = params.get('status') ?? '';
  const page = Number(params.get('page') ?? '1') || 1;
  const pageSize = Number(params.get('pageSize') ?? '25') || 25;
  const set = (patch: Record<string, string | number | null>) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(patch)) {
          if (v === null || v === '') next.delete(k);
          else next.set(k, String(v));
        }
        if (!('page' in patch)) next.delete('page');
        return next;
      },
      { replace: true },
    );
  const clear = () =>
    setParams((prev) => {
      const tab = prev.get('tab');
      return tab ? { tab } : new URLSearchParams();
    });
  return { q, status, page, pageSize, set, clear };
}

function useNewAction({ perm, label, to }: { perm: string; label: string; to: string }) {
  const { can } = useAuth();
  const navigate = useNavigate();
  return can(perm) ? { label, onClick: () => void navigate(to) } : undefined;
}

function SalesReturnsList() {
  const exportSelection = useExportSelection('sales_return');
  const { format } = useShop();
  const navigate = useNavigate();
  const { q, page, pageSize, set, clear } = useListParams();
  const list = useQuery({
    queryKey: ['sales-returns', 'list', { q, page, pageSize }],
    queryFn: () =>
      unwrap(
        api.GET('/sales-returns', { params: { query: { q: q || undefined, page, pageSize } } }),
      ),
    placeholderData: keepPreviousData,
  });
  const action = useNewAction({
    perm: 'sales.return',
    label: 'New sales return',
    to: '/returns/sales/new',
  });
  const fileCounts = useAttachmentCounts(
    'SALES_RETURN',
    (list.data?.data ?? []).map((r) => r.id),
  );
  const columns: Column<SalesReturnRow>[] = [
    {
      key: 'number',
      header: 'Return',
      render: (r) => (
        <Cell2
          primary={r.number}
          secondary={
            <DateWithParty date={businessDate(r.docDate, format)} party={r.contact.name} />
          }
        />
      ),
    },
    {
      key: 'party',
      header: 'Customer',
      hideBelow: 'sm',
      render: (r) => <Cell2 primary={r.contact.name} secondary={r.reason ?? r.contact.phone} />,
    },
    {
      key: 'invoice',
      header: 'Invoice',
      hideBelow: 'md',
      render: (r) => (
        <DocLink type="SALES_INVOICE" id={r.salesInvoice.id} number={r.salesInvoice.number} />
      ),
    },
    {
      key: 'lines',
      header: 'Lines',
      align: 'right',
      hideBelow: 'lg',
      render: (r) => <span className="tabular">{r.lineCount}</span>,
    },
    {
      key: 'note',
      header: 'Credit note',
      align: 'right',
      render: (r) =>
        r.creditNote ? (
          <Box>
            <Money value={r.creditNote.total} strong />
            <Typography variant="caption" color="text.secondary" component="div">
              {r.creditNote.number}
            </Typography>
          </Box>
        ) : (
          '—'
        ),
    },
  ];
  if (list.isError) return <ErrorState error={list.error} onRetry={() => void list.refetch()} />;
  return (
    <>
      <DataTable
        {...exportSelection.table}
        columns={[...columns, ...filesColumn<SalesReturnRow>(fileCounts)]}
        rows={list.data?.data}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        page={list.data ? { page, pageSize, total: list.data.page.total } : undefined}
        onPageChange={(p) => set({ page: p })}
        onPageSizeChange={(s) => set({ pageSize: s, page: null })}
        onRowClick={(r) => void navigate(links.salesReturn(r.id))}
        toolbar={
          <SearchField
            value={q}
            onChange={(v) => set({ q: v })}
            placeholder="Search number or customer"
          />
        }
        empty={
          q ? (
            <EmptyState
              icon={<AssignmentReturnRounded />}
              title="No matching returns"
              body="Try another search."
              action={{ label: 'Clear search', onClick: clear }}
            />
          ) : (
            <EmptyState
              icon={<AssignmentReturnRounded />}
              title="No sales returns yet"
              body="When a customer brings goods back, record it against their invoice — stock comes back and they get a credit note."
              action={action}
            />
          )
        }
      />
      {exportSelection.dialog}
    </>
  );
}

function PurchaseReturnsList() {
  const exportSelection = useExportSelection('purchase_return');
  const { format } = useShop();
  const navigate = useNavigate();
  const { q, page, pageSize, set, clear } = useListParams();
  const list = useQuery({
    queryKey: ['purchase-returns', 'list', { q, page, pageSize }],
    queryFn: () =>
      unwrap(
        api.GET('/purchase-returns', { params: { query: { q: q || undefined, page, pageSize } } }),
      ),
    placeholderData: keepPreviousData,
  });
  const action = useNewAction({
    perm: 'purchases.return',
    label: 'New purchase return',
    to: '/returns/purchase/new',
  });
  const fileCounts = useAttachmentCounts(
    'PURCHASE_RETURN',
    (list.data?.data ?? []).map((r) => r.id),
  );
  const columns: Column<PurchaseReturnRow>[] = [
    {
      key: 'number',
      header: 'Return',
      render: (r) => (
        <Cell2
          primary={r.number}
          secondary={
            <DateWithParty date={businessDate(r.docDate, format)} party={r.contact.name} />
          }
        />
      ),
    },
    {
      key: 'party',
      header: 'Supplier',
      hideBelow: 'sm',
      render: (r) => <Cell2 primary={r.contact.name} secondary={r.reason ?? r.location.name} />,
    },
    {
      key: 'receipt',
      header: 'Receipt',
      hideBelow: 'md',
      render: (r) => r.goodsReceipt.number,
    },
    {
      key: 'notes',
      header: 'Debit notes',
      hideBelow: 'sm',
      render: (r) =>
        r.debitNotes.length ? (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {r.debitNotes.map((n) => (
              <Chip key={n.id} size="small" variant="outlined" label={n.number} />
            ))}
          </Box>
        ) : (
          <Typography variant="caption" color="text.secondary">
            Unbilled goods
          </Typography>
        ),
    },
    {
      key: 'value',
      header: 'Stock out',
      align: 'right',
      render: (r) => <Money value={r.totalCost} strong />,
    },
  ];
  if (list.isError) return <ErrorState error={list.error} onRetry={() => void list.refetch()} />;
  return (
    <>
      <DataTable
        {...exportSelection.table}
        columns={[...columns, ...filesColumn<PurchaseReturnRow>(fileCounts)]}
        rows={list.data?.data}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        page={list.data ? { page, pageSize, total: list.data.page.total } : undefined}
        onPageChange={(p) => set({ page: p })}
        onPageSizeChange={(s) => set({ pageSize: s, page: null })}
        onRowClick={(r) => void navigate(links.purchaseReturn(r.id))}
        toolbar={
          <SearchField
            value={q}
            onChange={(v) => set({ q: v })}
            placeholder="Search number or supplier"
          />
        }
        empty={
          q ? (
            <EmptyState
              icon={<UndoRounded />}
              title="No matching returns"
              body="Try another search."
              action={{ label: 'Clear search', onClick: clear }}
            />
          ) : (
            <EmptyState
              icon={<UndoRounded />}
              title="No purchase returns yet"
              body="Send damaged or wrong goods back to the supplier from the delivery they came on."
              action={action}
            />
          )
        }
      />
      {exportSelection.dialog}
    </>
  );
}

type NoteRow = CreditNoteRow | DebitNoteRow;

function NotesList({ kind }: { kind: 'credit' | 'debit' }) {
  const exportSelection = useExportSelection(kind === 'credit' ? 'credit_note' : 'debit_note');
  const { format } = useShop();
  const navigate = useNavigate();
  const { q, status, page, pageSize, set, clear } = useListParams();
  const st: 'POSTED' | 'CANCELLED' | undefined =
    status === 'POSTED' || status === 'CANCELLED' ? status : undefined;
  const list = useQuery({
    queryKey: [
      kind === 'credit' ? 'credit-notes' : 'debit-notes',
      'list',
      { q, st, page, pageSize },
    ],
    queryFn: (): Promise<{ data: NoteRow[]; page: { total: number } }> => {
      const query = { q: q || undefined, status: st, page, pageSize };
      return kind === 'credit'
        ? unwrap(api.GET('/credit-notes', { params: { query } }))
        : unwrap(api.GET('/debit-notes', { params: { query } }));
    },
    placeholderData: keepPreviousData,
  });
  const action = useNewAction({
    perm: kind === 'credit' ? 'sales.return' : 'purchases.return',
    label: kind === 'credit' ? 'New credit note' : 'New debit note',
    to: `/returns/${kind}-notes/new`,
  });
  const source = (r: NoteRow) =>
    'salesInvoice' in r
      ? { type: 'SALES_INVOICE', ...r.salesInvoice }
      : { type: 'SUPPLIER_INVOICE', ...r.supplierInvoice };
  const fromReturn = (r: NoteRow) =>
    'salesReturnId' in r ? Boolean(r.salesReturnId) : Boolean(r.purchaseReturnId);

  const fileCounts = useAttachmentCounts(
    kind === 'credit' ? 'CREDIT_NOTE' : 'DEBIT_NOTE',

    (list.data?.data ?? []).map((r) => r.id),
  );

  const columns: Column<NoteRow>[] = [
    {
      key: 'number',
      header: kind === 'credit' ? 'Credit note' : 'Debit note',
      render: (r) => (
        <Cell2
          primary={
            <Box
              component="span"
              sx={{ textDecoration: r.status === 'CANCELLED' ? 'line-through' : undefined }}
            >
              {r.number}
            </Box>
          }
          secondary={
            <DateWithParty date={businessDate(r.docDate, format)} party={r.contact.name} />
          }
        />
      ),
    },
    {
      key: 'party',
      header: kind === 'credit' ? 'Customer' : 'Supplier',
      hideBelow: 'sm',
      render: (r) => <Cell2 primary={r.contact.name} secondary={r.reason ?? undefined} />,
    },
    {
      key: 'source',
      header: kind === 'credit' ? 'Invoice' : 'Bill',
      hideBelow: 'md',
      render: (r) => {
        const s = source(r);
        return <DocLink type={s.type} id={s.id} number={s.number} />;
      },
    },
    {
      key: 'origin',
      header: 'Kind',
      hideBelow: 'lg',
      render: (r) => (
        <Chip
          size="small"
          variant="outlined"
          label={fromReturn(r) ? 'Return' : 'Adjustment'}
          color={fromReturn(r) ? 'info' : 'default'}
        />
      ),
    },
    {
      key: 'applied',
      header: 'Applied',
      hideBelow: 'sm',
      width: 170,
      render: (r) =>
        r.status === 'CANCELLED' ? (
          <Typography variant="caption" color="text.disabled">
            Reversed
          </Typography>
        ) : (
          <Box>
            <AllocationBar
              value={percent(r.allocated, r.total)}
              tone={Number(r.unallocated) > 0 ? 'warning' : 'success'}
            />
            <Typography variant="caption" color="text.secondary" className="tabular">
              {Number(r.unallocated) > 0
                ? `${money(r.unallocated, format)} credit left`
                : 'Fully applied'}
            </Typography>
          </Box>
        ),
    },
    {
      key: 'total',
      header: 'Total',
      align: 'right',
      render: (r) => (
        <Box
          sx={{
            color: r.status === 'CANCELLED' ? 'text.disabled' : undefined,
            textDecoration: r.status === 'CANCELLED' ? 'line-through' : undefined,
          }}
        >
          <Money value={r.total} strong />
        </Box>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      hideBelow: 'sm',
      render: (r) => <StatusChip status={r.status} />,
    },
  ];
  if (list.isError) return <ErrorState error={list.error} onRetry={() => void list.refetch()} />;
  return (
    <>
      <DataTable
        {...exportSelection.table}
        columns={[...columns, ...filesColumn<NoteRow>(fileCounts)]}
        rows={list.data?.data}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        page={list.data ? { page, pageSize, total: list.data.page.total } : undefined}
        onPageChange={(p) => set({ page: p })}
        onPageSizeChange={(s) => set({ pageSize: s, page: null })}
        onRowClick={(r) =>
          void navigate(kind === 'credit' ? links.creditNote(r.id) : links.debitNote(r.id))
        }
        toolbar={
          <>
            <SearchField
              value={q}
              onChange={(v) => set({ q: v })}
              placeholder={`Search number or ${kind === 'credit' ? 'customer' : 'supplier'}`}
            />
            <Box sx={{ flex: 1 }} />
            <TextField
              select
              size="small"
              value={st ?? ''}
              onChange={(e) => set({ status: e.target.value })}
              fullWidth={false}
              sx={{ minWidth: 150, maxWidth: { sm: 200 } }}
              slotProps={{ select: { displayEmpty: true }, htmlInput: { 'aria-label': 'Status' } }}
            >
              <MenuItem value="">Any status</MenuItem>
              <MenuItem value="POSTED">Posted</MenuItem>
              <MenuItem value="CANCELLED">Cancelled</MenuItem>
            </TextField>
          </>
        }
        empty={
          q || st ? (
            <EmptyState
              icon={<ReceiptLongRounded />}
              title="No matching notes"
              body="Try another search or clear the filter."
              action={{ label: 'Clear filters', onClick: clear }}
            />
          ) : (
            <EmptyState
              icon={<ReceiptLongRounded />}
              title={kind === 'credit' ? 'No credit notes yet' : 'No debit notes yet'}
              body={
                kind === 'credit'
                  ? 'Credit notes come from sales returns, or lower an invoice directly — a price correction or an allowance.'
                  : 'Debit notes come from purchase returns, or record a credit the supplier gave you on a bill.'
              }
              action={action}
            />
          )
        }
      />
      {exportSelection.dialog}
    </>
  );
}
